import { Clock, Eye, Shield } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { AccessCardField } from "./AccessCardField";
import { AccessEffectiveCell } from "./AccessEffectiveCell";
import { AccessUsersEmpty } from "./AccessUsersEmpty";
import { type AccessUsersListProps, canOpenRolesFor } from "./accessUsersList.types";
import { PrincipalAvatar } from "./PrincipalAvatar";
import { PrincipalRolesCell } from "./PrincipalRolesCell";

/**
 * Mobile face of the users list, rendered below `lg`. Desktop is `AccessUsersTable`; both take
 * `AccessUsersListProps`, so the two cannot drift apart.
 *
 * This exists because the table's grid is three fixed tracks wide (442px) inside an `overflow-hidden`
 * wrapper, which on a phone does not truncate — it clips "Effective access" and the row's only action out of
 * existence, leaving no way to open the assign-roles drawer at all.
 *
 * Two things the table can only express as a `title`, which a touch screen cannot reach, are rendered as
 * visible text here: why effective access is unknown, and how many roles the "+N" chip stands for. On a
 * security surface an unexplained "?" is the worst case — the whole point of that glyph is that it means
 * "unknown", not "no access", and the distinction has to survive on a phone.
 */
export function AccessUsersCards({
  users,
  rolesByUser,
  rolesUnavailable,
  summaryById,
  catalogUnavailable,
  canManage,
  currentUserId,
  loadFailed,
  onOpenRoles,
}: AccessUsersListProps) {
  const { t } = useTranslations("settings");

  if (users.length === 0) {
    return <AccessUsersEmpty loadFailed={loadFailed} />;
  }

  return (
    /* No outer border or padding: the host `SettingsSection` already draws the card and its `p-[22px]`,
       and each ColocaSurface below draws its own frame. Nesting another one doubles the hairline. */
    <div className="flex flex-col gap-3">
      {users.map((u) => {
        const assigned = rolesByUser[u.id] ?? [];
        const summary = summaryById[u.id];
        const rolesFailed = rolesUnavailable.has(u.id);
        const noRoles = !rolesFailed && assigned.length === 0;
        const isMe = !!currentUserId && u.id === currentUserId;
        const isExternal = u.user_type === "organization_member";
        const isPending = u.status === "pending_verification";
        const openRolesDrawer = canOpenRolesFor(u.id, catalogUnavailable, rolesUnavailable)
          ? () => onOpenRoles(u)
          : undefined;

        return (
          <ColocaSurface
            key={u.id}
            padding="md"
            className={cn(
              "flex flex-col gap-3",
              u.status === "suspended" && "opacity-55",
              isMe && "bg-primary/5",
            )}
          >
            {/* Identity takes the full width here, unlike the table where it shares the row with three
                other tracks — the email is what identifies a person and it is the first thing the grid
                truncated. */}
            <div className="flex min-w-0 items-start gap-2.5">
              <PrincipalAvatar name={u.full_name} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="text-[13px] font-medium text-foreground">{u.full_name}</span>
                  {isMe && <ColocaBadge variant="primary">{t("access_badge_you")}</ColocaBadge>}
                  {isExternal && <ColocaBadge variant="secondary">{t("access_badge_external")}</ColocaBadge>}
                  {isPending && (
                    <ColocaBadge variant="warning" icon={Clock}>
                      {t("access_badge_pending")}
                    </ColocaBadge>
                  )}
                </div>
                <div className="mt-0.5 truncate font-mono text-[11px] text-muted-foreground">{u.email}</div>
              </div>
            </div>

            <AccessCardField label={t("access_col_roles")}>
              <PrincipalRolesCell roles={assigned} rolesFailed={rolesFailed} onShowAll={openRolesDrawer} />
            </AccessCardField>

            <AccessCardField label={t("access_col_effective")}>
              {/* `explainUnknown`: the table hides the reason in a `title`, which a phone cannot reach. */}
              <AccessEffectiveCell
                summary={summary}
                noRoles={noRoles}
                rolesFailed={rolesFailed}
                catalogUnavailable={catalogUnavailable}
                explainUnknown
              />
            </AccessCardField>

            {/* The row's single action, full width at the foot of the card: it is the only way into the
                assign-roles drawer, and on the grid it was the first thing clipped off-screen. */}
            {openRolesDrawer && (
              <div className="border-t border-dashed border-border pt-2.5">
                <ColocaButton
                  variant="default"
                  size="sm"
                  icon={canManage ? Shield : Eye}
                  onClick={openRolesDrawer}
                  className="w-full"
                >
                  {canManage ? t("access_action_roles") : t("access_action_view")}
                </ColocaButton>
              </div>
            )}
          </ColocaSurface>
        );
      })}
    </div>
  );
}
